import { ApiClient } from '../client';
import type { ApiResponse } from '../types';

export interface InvitationDetails {
  email: string;
  given_name: string;
  surname: string;
  organisation_name: string;
  expires_at: string;
}

export interface PendingInvitation {
  invitation_id: string;
  email: string;
  invited_by: string;
  created_at: string;
  expires_at: string;
  sent_at: string | null;
  resent_count: number;
  is_expired: boolean;
}

export interface InvitationListResponse {
  invitations: PendingInvitation[];
  pagination: {
    page: number;
    limit: number;
    total_count: number;
    total_pages: number;
    has_more: boolean;
  };
}

export interface CreateInvitationRequest {
  email: string;
  given_name: string;
  surname: string;
  job_title?: string;
  role_name: 'CustomerAdmin' | 'CampaignManager';
  organisation_scope?: string;
}

export interface AcceptInvitationRequest {
  token: string;
  password: string;
  accept_terms: boolean;
  accept_privacy: boolean;
}

export class InvitationApi {
  constructor(private client: ApiClient) {}

  /**
   * Get invitation details by token (public endpoint)
   */
  async getByToken(token: string): Promise<InvitationDetails> {
    const response = await this.client.get<any>(`/v1/Invitations/token/${token}`);
    return response?.data || response;
  }

  /**
   * Accept an invitation (public endpoint)
   */
  async accept(request: AcceptInvitationRequest): Promise<ApiResponse<{ 
    user_id: string; 
    email: string; 
    display_name: string; 
    message: string 
  }>> {
    return this.client.post('/v1/Invitations/accept', request);
  }

  /**
   * Get pending invitations for current customer
   */
  async getPending(page = 1, limit = 50): Promise<InvitationListResponse> {
    const response = await this.client.get<any>('/v1/Invitations', { page, limit });
    return response?.data || response;
  }

  /**
   * Create and send a new invitation
   */
  async create(request: CreateInvitationRequest): Promise<ApiResponse<{ 
    invitation_id: string; 
    user_id: string; 
    email: string; 
    expires_at: string; 
    email_sent: boolean 
  }>> {
    return this.client.post('/v1/Invitations', request);
  }

  /**
   * Resend an invitation email
   */
  async resend(invitationId: string): Promise<ApiResponse<{ message: string }>> {
    return this.client.post(`/v1/Invitations/${invitationId}/resend`, {});
  }

  /**
   * Cancel a pending invitation
   */
  async cancel(invitationId: string): Promise<void> {
    return this.client.delete(`/v1/Invitations/${invitationId}`);
  }
}
